import { EXPORT_LONG_EDGE, type ExportQuality } from '@shared/plans';
import { downscaleToLongEdge } from './CapturePasses';
import type { ImageSize } from './ImageSpec';

/**
 * The plan's ceiling on the resolution of an image leaving the app (phase 6.5).
 *
 * ## Why the ceiling is on the download and not on the capture
 *
 * A shot is captured at a size the enhance endpoint accepts, because every shot has to stay
 * directly enhanceable — see `nearestSizePreset`. Those sizes top out at 1536, so capping the
 * *capture* by plan would mean a Free account could not enhance at all, which is precisely the
 * opposite of what the free tier is for: 20 credits exist so somebody can reach the moment the
 * product is for, once.
 *
 * So the ceiling is applied to the bytes, on their way out. The frame on screen, the enhanced
 * render, the credits it cost and the shot in the gallery are all identical on every plan; what
 * differs is the size of the file you can take away.
 *
 * ## What it deliberately does not cover
 *
 * An anonymous user has no plan and gets no ceiling. That is not an oversight and not a loophole
 * worth closing: without an account there is no cloud sync and no included AI, the renders are on
 * the user's own OpenAI key, and this is the original single-user tool. Shrinking its output would
 * be gating the standalone product on a subscription it does not use.
 */

/** The size a download will actually be. Never upscales — a small capture stays its own size. */
export function exportSize(size: ImageSize, quality: ExportQuality): ImageSize {
  const ceiling = EXPORT_LONG_EDGE[quality];
  const longest = Math.max(size.width, size.height);
  if (longest <= ceiling || longest <= 0) return size;

  const scale = ceiling / longest;
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  };
}

/** Whether this plan would shrink an image of this size, i.e. whether the ceiling is doing work. */
export function isCapped(size: ImageSize, quality: ExportQuality): boolean {
  return Math.max(size.width, size.height) > EXPORT_LONG_EDGE[quality];
}

/**
 * A sentence for a download button whose output is smaller than the frame it came from.
 *
 * Silence would be the worse choice by a distance: a designer who downloads a 1536px render and
 * finds 1024px on disk has been given a file that is wrong for their purpose without being told
 * why, and will reasonably conclude the app is broken rather than that they are on the Free tier.
 */
export function describeExport(size: ImageSize, quality: ExportQuality): string {
  const output = exportSize(size, quality);
  const dimensions = `${String(output.width)}×${String(output.height)}`;
  return isCapped(size, quality)
    ? `Download at ${dimensions} — your plan's export size. The frame itself is ${String(size.width)}×${String(size.height)}.`
    : `Download at ${dimensions}`;
}

/**
 * Apply the ceiling to a PNG, returning the source untouched when it already fits.
 *
 * Re-encoding an image that needs no resizing would cost a decode, a canvas and a second encode to
 * produce a file the same size as the one already in hand — and would quietly strip a capture down
 * to whatever the canvas happens to preserve.
 */
export async function toExportBlob(
  source: Blob,
  size: ImageSize,
  quality: ExportQuality,
): Promise<Blob> {
  if (!isCapped(size, quality)) return source;
  return downscaleToLongEdge(source, EXPORT_LONG_EDGE[quality]);
}
